import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  LinkIcon,
  PencilIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";

import {
  fetchOrgChart,
  fetchOrgChartRevision,
  fetchOrgChartRevisions,
  createOrgChart,
  updateOrgChart,
  createOrgChartRevision,
  approveOrgChartRevision,
  updateOrgChartNodes,
  updateOrgChartLinks,
} from "../../../../api/quality/orgCharts";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PMCustomSelector } from "../../../../components/utils/PMCustomSelector";
import { fetchUsers } from "../../../../api/user";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { PMDialog } from "../../../../components/utils/PMDialog";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";

export const OrgChartEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const canCreate = useUserHasAccess({ role: "quality", action: "create" });
  const canUpdate = useUserHasAccess({ role: "quality", action: "update" });
  const canApprove = useUserHasAccess({ role: "quality", action: "approve" });
  const canvasRef = useRef(null);
  const [orgChart, setOrgChart] = useState(null);
  const [revision, setRevision] = useState(null);
  const [allRevisions, setAllRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [linkFromNode, setLinkFromNode] = useState(null);
  const [linkFromSide, setLinkFromSide] = useState(null); // 'top', 'right', 'bottom', 'left'
  const [linkMousePos, setLinkMousePos] = useState({ x: 0, y: 0 });
  const [hoveredConnectionPoint, setHoveredConnectionPoint] = useState(null); // { nodeId, side }
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [linkContextMenu, setLinkContextMenu] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);

  useEffect(() => {
    if (!isNew) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const orgChartData = await fetchOrgChart(id);
      setOrgChart(orgChartData);

      // Load all revisions
      const revisionsData = await fetchOrgChartRevisions(id);
      console.log("Fetched revisions:", revisionsData);
      const revisionsList = revisionsData.data || [];
      console.log("Revisions list:", revisionsList);
      setAllRevisions(revisionsList);

      // Only create new revision if NO revisions exist at all
      if (revisionsList.length === 0) {
        // No revisions exist, create one
        const newRevision = await createOrgChartRevision(id);
        setRevision(newRevision);
        const nodesWithDefaults = (newRevision.nodes || []).map(node => ({
          ...node,
          user_ids: node.user_ids || [],
          users: node.users || [],
        }));
        setNodes(nodesWithDefaults);
        setLinks(newRevision.links || []);
        // Reload revisions list
        const updatedRevisions = await fetchOrgChartRevisions(id);
        setAllRevisions(updatedRevisions.data || []);
      } else {
        // Priority: 1) Approved revision, 2) Latest non-approved revision, 3) Latest revision
        const approvedRevision = revisionsList.find((r) => r.is_approved);
        const latestNonApproved = revisionsList.find((r) => !r.is_approved);
        const revisionToLoad = approvedRevision || latestNonApproved || revisionsList[0];

        if (revisionToLoad) {
          const revisionData = await fetchOrgChartRevision(id, revisionToLoad.id);
          setRevision(revisionData);
          // Ensure user_ids and users arrays exist
          const nodesWithDefaults = (revisionData.nodes || []).map(node => ({
            ...node,
            user_ids: node.user_ids || [],
            users: node.users || [],
          }));
          setNodes(nodesWithDefaults);
          setLinks(revisionData.links || []);
        }
      }
    } catch (err) {
      console.error("Error loading org chart:", err);
      setError("Failed to load org chart");
      toast.error(t("Kunne ikke laste organisasjonskart"));
    } finally {
      setLoading(false);
    }
  };

  const loadRevision = async (revisionId) => {
    try {
      const revisionData = await fetchOrgChartRevision(id, revisionId);
      setRevision(revisionData);
      // Ensure user_ids and users arrays exist
      const nodesWithDefaults = (revisionData.nodes || []).map(node => ({
        ...node,
        user_ids: node.user_ids || [],
        users: node.users || [],
      }));
      setNodes(nodesWithDefaults);
      setLinks(revisionData.links || []);
      setSelectedNode(null);
      setSelectedLink(null);
    } catch (err) {
      console.error("Error loading revision:", err);
      toast.error(t("Kunne ikke laste revisjon"));
    }
  };

  const handleSave = async (values) => {
    try {
      if (isNew) {
        if (!canCreate) {
          toast.error(t("Du har ikke tilgang til å opprette organisasjonskart"));
          return;
        }
        const orgChartData = await createOrgChart(values);
        toast.success(t("Organisasjonskart ble opprettet"));
        navigate(`/dashboard/quality/org-charts/${orgChartData.id}/edit`);
        return;
      }

      if (!canUpdate) {
        toast.error(t("Du har ikke tilgang til å oppdatere organisasjonskart"));
        return;
      }

      // Update org chart metadata
      await updateOrgChart({ id, ...values });
      toast.success(t("Organisasjonskart ble oppdatert"));

      // Check if current revision is approved
      if (revision?.is_approved) {
        toast.error(t("Kan ikke redigere en godkjent revisjon. Opprett en ny revisjon først."));
        return;
      }

      // Save nodes and links to current revision (don't create new revision)
      if (revision) {
        // Prepare nodes for API - preserve existing IDs if they exist
        const nodesForApi = nodes.map((node) => {
          const nodeData = {
            user_id: node.user_id,
            user_ids: node.user_ids || [],
            title: node.title,
            description: node.description || "",
            x_position: node.x_position,
            y_position: node.y_position,
            width: node.width,
            height: node.height,
            color: node.color,
            z_index: node.z_index || 0,
          };
          // Only include id if it's a number (real database ID)
          if (node.id && typeof node.id === 'number') {
            nodeData.id = node.id;
          }
          return nodeData;
        });

        const nodesResponse = await updateOrgChartNodes(id, revision.id, nodesForApi);

        // Update nodes with real IDs from response
        if (nodesResponse.nodes) {
          // Ensure user_ids and users arrays exist
          const nodesWithDefaults = nodesResponse.nodes.map(node => ({
            ...node,
            user_ids: node.user_ids || [],
            users: node.users || [],
          }));
          setNodes(nodesWithDefaults);
          
          // Update links with real node IDs
          const nodeIdMap = {};
          nodes.forEach((oldNode, index) => {
            const newNode = nodesResponse.nodes[index];
            if (newNode && oldNode.id !== newNode.id) {
              nodeIdMap[oldNode.id] = newNode.id;
            }
          });

          const linksForApi = links
            .map((link) => ({
              from_node_id: nodeIdMap[link.from_node_id] || link.from_node_id,
              to_node_id: nodeIdMap[link.to_node_id] || link.to_node_id,
              from_side: link.from_side || 'bottom',
              to_side: link.to_side || 'top',
              link_type: link.link_type || "reports_to",
              color: link.color || "#6B7280",
              waypoints: link.waypoints || [],
            }))
            .filter((link) => {
              // Verify both nodes exist in the updated nodes
              const fromExists = nodesResponse.nodes.some(n => n.id === link.from_node_id);
              const toExists = nodesResponse.nodes.some(n => n.id === link.to_node_id);
              return fromExists && toExists && link.from_node_id && link.to_node_id;
            });

          if (linksForApi.length > 0) {
            await updateOrgChartLinks(id, revision.id, linksForApi);
          }
          
          // Reload revision to get updated links and nodes with user data
          const updatedRevision = await fetchOrgChartRevision(id, revision.id);
          const finalNodesWithDefaults = (updatedRevision.nodes || []).map(node => ({
            ...node,
            user_ids: node.user_ids || [],
            users: node.users || [],
          }));
          setNodes(finalNodesWithDefaults);
          setLinks(updatedRevision.links || []);
        }

        toast.success(t("Moduler ble lagret"));
      }
    } catch (err) {
      console.error("Error saving org chart:", err);
      toast.error(t("Kunne ikke lagre organisasjonskart"));
    }
  };

  const handleCreateRevision = async () => {
    if (!canUpdate) {
      toast.error(t("Du har ikke tilgang til å opprette nye revisjoner"));
      return;
    }
    try {
      const newRevision = await createOrgChartRevision(id);
      // Reload full revision data
      const revisionData = await fetchOrgChartRevision(id, newRevision.id);
      setRevision(revisionData);
      const nodesWithDefaults = (revisionData.nodes || []).map(node => ({
        ...node,
        user_ids: node.user_ids || [],
        users: node.users || [],
      }));
      setNodes(nodesWithDefaults);
      setLinks(revisionData.links || []);
      setSelectedNode(null);
      setSelectedLink(null);
      
      // Reload revisions list
      const updatedRevisions = await fetchOrgChartRevisions(id);
      setAllRevisions(updatedRevisions.data || []);
      
      toast.success(t("Ny revisjon ble opprettet"));
    } catch (err) {
      console.error("Error creating revision:", err);
      toast.error(t("Kunne ikke opprette revisjon"));
    }
  };

  const handleApproveRevision = async () => {
    try {
      await approveOrgChartRevision(id, revision.id);
      await loadData();
      toast.success(t("Revisjon ble godkjent"));
    } catch (err) {
      toast.error(t("Kunne ikke godkjenne revisjon"));
    }
  };

  const handleAddNode = () => {
    const newNode = {
      id: `node-${Date.now()}`,
      title: t("Ny modul"),
      description: "",
      x_position: 100,
      y_position: 100,
      width: 200,
      height: 100,
      color: "#3B82F6",
      z_index: 0,
      user_ids: [],
      users: [],
    };
    setNodes([...nodes, newNode]);
    setSelectedNode(newNode);
    setEditDialogOpen(true);
    setEditingNode(newNode);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes(nodes.filter((n) => n.id !== nodeId));
    setLinks(
      links.filter(
        (l) => l.from_node_id !== nodeId && l.to_node_id !== nodeId
      )
    );
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
      setEditingNode(null);
      setEditDialogOpen(false);
    }
    setContextMenu(null);
  };

  const handleEditNode = (node) => {
    setEditingNode({ ...node });
    setEditDialogOpen(true);
    setContextMenu(null);
  };

  const handleSaveNodeEdit = () => {
    if (!editingNode) return;
    
    const updatedNodes = nodes.map((n) =>
      n.id === editingNode.id ? editingNode : n
    );
    setNodes(updatedNodes);
    setSelectedNode(editingNode);
    setEditDialogOpen(false);
    setEditingNode(null);
  };

  const handleStartLink = (nodeId, side, e) => {
    if (isReadOnly) return;
    e.stopPropagation();
    setIsLinking(true);
    setLinkFromNode(nodeId);
    setLinkFromSide(side);
    setContextMenu(null);
  };

  const handleCompleteLink = (nodeId, side, e) => {
    if (!isLinking || !linkFromNode || !linkFromSide) return;
    e.stopPropagation();
    
    if (linkFromNode === nodeId && linkFromSide === side) {
      // Same connection point - cancel linking
      setIsLinking(false);
      setLinkFromNode(null);
      setLinkFromSide(null);
      return;
    }

    // Check if link already exists
    const linkExists = links.some(
      (l) =>
        (l.from_node_id === linkFromNode && l.to_node_id === nodeId && l.from_side === linkFromSide && l.to_side === side) ||
        (l.from_node_id === nodeId && l.to_node_id === linkFromNode && l.from_side === side && l.to_side === linkFromSide)
    );

    if (!linkExists) {
      const newLink = {
        id: `link-${Date.now()}`,
        from_node_id: linkFromNode,
        to_node_id: nodeId,
        from_side: linkFromSide,
        to_side: side,
        link_type: "reports_to",
        color: "#6B7280",
      };
      setLinks([...links, newLink]);
    }
    setIsLinking(false);
    setLinkFromNode(null);
    setLinkFromSide(null);
  };

  const getConnectionPointPosition = (node, side) => {
    switch (side) {
      case 'top':
        return {
          x: node.x_position + node.width / 2,
          y: node.y_position,
        };
      case 'right':
        return {
          x: node.x_position + node.width,
          y: node.y_position + node.height / 2,
        };
      case 'bottom':
        return {
          x: node.x_position + node.width / 2,
          y: node.y_position + node.height,
        };
      case 'left':
        return {
          x: node.x_position,
          y: node.y_position + node.height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  };

  const handleDeleteLink = (linkId) => {
    setLinks(links.filter((l) => l.id !== linkId));
    if (selectedLink?.id === linkId) {
      setSelectedLink(null);
    }
  };

  const handleNodeMouseDown = (e, node) => {
    e.stopPropagation();
    
    if (e.button === 2) {
      // Right click
      e.preventDefault();
      e.stopPropagation();
      setSelectedNode(node);
      setSelectedLink(null);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        node: node,
      });
      return;
    }

    // Left click - normal selection and drag
    setSelectedNode(node);
    setSelectedLink(null);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left - node.x_position,
      y: e.clientY - rect.top - node.y_position,
    });
    setDraggedNode(node);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Update mouse position for linking
      if (isLinking) {
        setLinkMousePos({ x, y });
        
        // Check if hovering over a connection point
        let foundHover = null;
        for (const node of nodes) {
          const sides = ['top', 'right', 'bottom', 'left'];
          for (const side of sides) {
            const pos = getConnectionPointPosition(node, side);
            const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));
            if (distance < 15) { // Within 15px of connection point
              foundHover = { nodeId: node.id, side };
              break;
            }
          }
          if (foundHover) break;
        }
        setHoveredConnectionPoint(foundHover);
      }

      if (isResizing && selectedNode && resizeHandle) {
        const deltaX = x - selectedNode.x_position;
        const deltaY = y - selectedNode.y_position;

        let newWidth = selectedNode.width;
        let newHeight = selectedNode.height;
        let newX = selectedNode.x_position;
        let newY = selectedNode.y_position;

        if (resizeHandle.includes("e")) {
          newWidth = Math.max(100, deltaX);
        }
        if (resizeHandle.includes("w")) {
          newWidth = Math.max(100, selectedNode.width - deltaX);
          newX = selectedNode.x_position + (selectedNode.width - newWidth);
        }
        if (resizeHandle.includes("s")) {
          newHeight = Math.max(60, deltaY);
        }
        if (resizeHandle.includes("n")) {
          newHeight = Math.max(60, selectedNode.height - deltaY);
          newY = selectedNode.y_position + (selectedNode.height - newHeight);
        }

        const updated = {
          ...selectedNode,
          width: newWidth,
          height: newHeight,
          x_position: newX,
          y_position: newY,
        };
        setSelectedNode(updated);
        setNodes(
          nodes.map((n) => (n.id === selectedNode.id ? updated : n))
        );
      } else if (draggedNode) {
        const newX = Math.max(0, x - dragOffset.x);
        const newY = Math.max(0, y - dragOffset.y);
        const updated = {
          ...draggedNode,
          x_position: newX,
          y_position: newY,
        };
        setSelectedNode(updated);
        setNodes(
          nodes.map((n) => (n.id === draggedNode.id ? updated : n))
        );
      }
    },
    [draggedNode, dragOffset, isResizing, selectedNode, resizeHandle, nodes, isLinking]
  );

  const handleMouseUp = useCallback((e) => {
    setDraggedNode(null);
    setIsResizing(false);
    setResizeHandle(null);
    
    // Cancel linking if clicking outside connection points
    if (isLinking) {
      // Check if click was on a connection point
      const clickedOnConnectionPoint = e.target.classList.contains('connection-point') || 
                                       e.target.closest('.connection-point');
      
      // If not clicking on a connection point, cancel linking
      if (!clickedOnConnectionPoint) {
        setIsLinking(false);
        setLinkFromNode(null);
        setLinkFromSide(null);
        setHoveredConnectionPoint(null);
      }
    }
  }, [isLinking]);

  useEffect(() => {
    if (draggedNode || isResizing || isLinking) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [draggedNode, isResizing, isLinking, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!contextMenu && !linkContextMenu) return;
    
    const handleClickOutside = (e) => {
      // Don't close if clicking inside the context menu
      if (e.target.closest('.context-menu-container')) {
        return;
      }
      setContextMenu(null);
      setLinkContextMenu(null);
    };
    
    // Use setTimeout to avoid immediate closure from the right-click event
    // Longer delay to ensure the menu is fully rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("click", handleClickOutside, true);
      document.addEventListener("contextmenu", handleClickOutside, true);
    }, 200);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("contextmenu", handleClickOutside, true);
    };
  }, [contextMenu, linkContextMenu]);

  const handleResizeStart = (e, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
  };

  const getNodeById = (nodeId) => {
    return nodes.find((n) => n.id === nodeId);
  };

  // Calculate orthogonal path (rectangular routing) between two points
  const calculateOrthogonalPath = (fromPos, toPos, fromSide, toSide, waypoints = []) => {
    // If waypoints exist, use them for manual routing
    if (waypoints && waypoints.length > 0) {
      let path = `M ${fromPos.x} ${fromPos.y}`;
      // Add each waypoint
      waypoints.forEach(waypoint => {
        path += ` L ${waypoint.x} ${waypoint.y}`;
      });
      // Finally go to target
      path += ` L ${toPos.x} ${toPos.y}`;
      return path;
    }
    
    const spacing = 30; // Spacing from nodes to avoid overlap
    
    let path = `M ${fromPos.x} ${fromPos.y}`;
    
    const horizontalDistance = Math.abs(toPos.x - fromPos.x);
    const verticalDistance = Math.abs(toPos.y - fromPos.y);
    
    // Determine if we can use a simpler 2-segment path (L-shape)
    // vs a 3-segment path (U-shape or Z-shape)
    
    // Case 1: Both connection points are on horizontal sides (top/bottom)
    if ((fromSide === 'top' || fromSide === 'bottom') && (toSide === 'top' || toSide === 'bottom')) {
      // If nodes are aligned horizontally enough, use simple L-shape
      if (horizontalDistance < 200) {
        // Simple vertical-horizontal L-shape
        const midY = (fromPos.y + toPos.y) / 2;
        path += ` L ${fromPos.x} ${midY}`;
        path += ` L ${toPos.x} ${midY}`;
        path += ` L ${toPos.x} ${toPos.y}`;
      } else {
        // Go further out to avoid crossing nodes
        const fromOffsetY = fromPos.y + (fromSide === 'bottom' ? spacing : -spacing);
        const toOffsetY = toPos.y + (toSide === 'bottom' ? spacing : -spacing);
        // Use the one that's further out
        const midY = Math.abs(fromOffsetY - fromPos.y) > Math.abs(toOffsetY - toPos.y) ? fromOffsetY : toOffsetY;
        path += ` L ${fromPos.x} ${midY}`;
        path += ` L ${toPos.x} ${midY}`;
        path += ` L ${toPos.x} ${toPos.y}`;
      }
    }
    // Case 2: Both connection points are on vertical sides (left/right)
    else if ((fromSide === 'left' || fromSide === 'right') && (toSide === 'left' || toSide === 'right')) {
      // If nodes are aligned vertically enough, use simple L-shape
      if (verticalDistance < 200) {
        // Simple horizontal-vertical L-shape
        const midX = (fromPos.x + toPos.x) / 2;
        path += ` L ${midX} ${fromPos.y}`;
        path += ` L ${midX} ${toPos.y}`;
        path += ` L ${toPos.x} ${toPos.y}`;
      } else {
        // Go further out to avoid crossing nodes
        const fromOffsetX = fromPos.x + (fromSide === 'right' ? spacing : -spacing);
        const toOffsetX = toPos.x + (toSide === 'right' ? spacing : -spacing);
        // Use the one that's further out
        const midX = Math.abs(fromOffsetX - fromPos.x) > Math.abs(toOffsetX - toPos.x) ? fromOffsetX : toOffsetX;
        path += ` L ${midX} ${fromPos.y}`;
        path += ` L ${midX} ${toPos.y}`;
        path += ` L ${toPos.x} ${toPos.y}`;
      }
    }
    // Case 3: Mixed sides - use smart routing
    else {
      // Determine which direction to go first based on which is more aligned
      if (fromSide === 'top' || fromSide === 'bottom') {
        // Start vertically
        const fromOffsetY = fromPos.y + (fromSide === 'bottom' ? spacing : -spacing);
        path += ` L ${fromPos.x} ${fromOffsetY}`;
        
        // Check if we can go directly to target's horizontal position
        if (toSide === 'left' || toSide === 'right') {
          // Target is on side, go horizontally then vertically
          path += ` L ${toPos.x} ${fromOffsetY}`;
          path += ` L ${toPos.x} ${toPos.y}`;
        } else {
          // Target is also top/bottom, use middle Y
          const midY = (fromOffsetY + toPos.y) / 2;
          path += ` L ${toPos.x} ${midY}`;
          path += ` L ${toPos.x} ${toPos.y}`;
        }
      } else {
        // Start horizontally
        const fromOffsetX = fromPos.x + (fromSide === 'right' ? spacing : -spacing);
        path += ` L ${fromOffsetX} ${fromPos.y}`;
        
        // Check if we can go directly to target's vertical position
        if (toSide === 'top' || toSide === 'bottom') {
          // Target is on top/bottom, go vertically then horizontally
          path += ` L ${fromOffsetX} ${toPos.y}`;
          path += ` L ${toPos.x} ${toPos.y}`;
        } else {
          // Target is also left/right, use middle X
          const midX = (fromOffsetX + toPos.x) / 2;
          path += ` L ${midX} ${toPos.y}`;
          path += ` L ${toPos.x} ${toPos.y}`;
        }
      }
    }
    
    return path;
  };

  const handleAddWaypoint = (link, e) => {
    if (isReadOnly || !canvasRef.current) return;
    e.stopPropagation();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Snap to nearest orthogonal position (align to grid)
    const snapGrid = 10;
    const snappedX = Math.round(x / snapGrid) * snapGrid;
    const snappedY = Math.round(y / snapGrid) * snapGrid;
    
    // Add waypoint to link
    const updatedLinks = links.map(l => {
      if (l.id === link.id) {
        const waypoints = l.waypoints || [];
        return {
          ...l,
          waypoints: [...waypoints, { x: snappedX, y: snappedY }]
        };
      }
      return l;
    });
    
    setLinks(updatedLinks);
    setSelectedLink({ ...link, waypoints: [...(link.waypoints || []), { x: snappedX, y: snappedY }] });
  };

  const handleDeleteWaypoint = (link, waypointIndex, e) => {
    if (isReadOnly) return;
    e.stopPropagation();
    
    const updatedLinks = links.map(l => {
      if (l.id === link.id) {
        const waypoints = l.waypoints || [];
        return {
          ...l,
          waypoints: waypoints.filter((_, index) => index !== waypointIndex)
        };
      }
      return l;
    });
    
    setLinks(updatedLinks);
    const updatedLink = updatedLinks.find(l => l.id === link.id);
    setSelectedLink(updatedLink);
  };

  const renderLink = (link) => {
    const fromNode = getNodeById(link.from_node_id);
    const toNode = getNodeById(link.to_node_id);
    if (!fromNode || !toNode) return null;

    const fromSide = link.from_side || 'bottom';
    const toSide = link.to_side || 'top';
    const fromPos = getConnectionPointPosition(fromNode, fromSide);
    const toPos = getConnectionPointPosition(toNode, toSide);

    // Calculate orthogonal path (with waypoints if they exist)
    const waypoints = link.waypoints || [];
    const pathData = calculateOrthogonalPath(fromPos, toPos, fromSide, toSide, waypoints);

    // Create a thicker invisible path for easier clicking
    const clickPadding = 8;
    const isSelected = selectedLink?.id === link.id;

    return (
      <g
        key={link.id}
        onClick={(e) => {
          if (isReadOnly) return;
          // Only select link if not clicking on waypoint
          if (!e.target.classList.contains('waypoint-circle')) {
            e.stopPropagation();
            setSelectedLink(link);
            setSelectedNode(null);
          }
        }}
        onDoubleClick={(e) => {
          if (isReadOnly) return;
          e.stopPropagation();
          handleAddWaypoint(link, e);
        }}
        onContextMenu={(e) => {
          if (isReadOnly) return;
          e.preventDefault();
          e.stopPropagation();
          setSelectedLink(link);
          setSelectedNode(null);
          setLinkContextMenu({
            x: e.clientX,
            y: e.clientY,
            link: link,
          });
        }}
        className="cursor-pointer"
      >
        {/* Invisible thicker path for easier clicking */}
        <path
          d={pathData}
          stroke="transparent"
          strokeWidth={clickPadding * 2}
          fill="none"
          style={{ cursor: 'pointer' }}
        />
        {/* Visible orthogonal path */}
        <path
          d={pathData}
          stroke={isSelected ? "#3B82F6" : link.color}
          strokeWidth={isSelected ? 3 : 2}
          fill="none"
        />
        
        {/* Render waypoints */}
        {waypoints.map((waypoint, index) => (
          <g key={`waypoint-${index}`}>
            {/* Waypoint circle */}
            <circle
              cx={waypoint.x}
              cy={waypoint.y}
              r={isSelected ? 6 : 4}
              fill={isSelected ? "#3B82F6" : "#6B7280"}
              stroke="white"
              strokeWidth={2}
              className="waypoint-circle"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWaypoint(link, index, e);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteWaypoint(link, index, e);
              }}
            />
            {/* Inner dot for better visibility */}
            {isSelected && (
              <circle
                cx={waypoint.x}
                cy={waypoint.y}
                r={2}
                fill="white"
                className="waypoint-circle"
              />
            )}
          </g>
        ))}
      </g>
    );
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(t("Tittel er påkrevd")),
  });

  const isReadOnly = revision?.is_approved;

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[error]} />;
  }

  const initialValues = orgChart
    ? {
        title: orgChart.title,
        description: orgChart.description || "",
        is_active: orgChart.is_active !== false,
      }
    : {
        title: "",
        description: "",
        is_active: true,
      };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Form>
          <DashboardHeader
            title={
              isNew
                ? t("Nytt organisasjonskart")
                : t("Rediger organisasjonskart")
            }
            subtitle=""
            mainButton={{
              label: t("Tilbake"),
              action: () => navigate(-1),
              path: null,
              color: "",
              icon: ArrowLeftIcon,
            }}
          >
            <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {/* Form Fields */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("Tittel")} *
                    </label>
                    <input
                      type="text"
                      value={values.title}
                      onChange={(e) => setFieldValue("title", e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("Beskrivelse")}
                    </label>
                    <textarea
                      value={values.description}
                      onChange={(e) =>
                        setFieldValue("description", e.target.value)
                      }
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              {/* Revision Info and Selector */}
              {revision && (
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                  {/* Header with status and dates at the top */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <span className="text-base font-semibold text-gray-900">
                        {t("Revisjon")} {revision.revision_number}
                      </span>
                      {revision.is_approved && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          {t("Godkjent")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-6 text-xs text-gray-600">
                      <div>
                        <span className="font-medium text-gray-700">{t("Opprettet")}:</span>{" "}
                        {new Date(revision.created_at).toLocaleString("no-NO", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      {revision.is_approved && revision.approved_at && (
                        <div>
                          <span className="font-medium text-gray-700">{t("Godkjent")}:</span>{" "}
                          {new Date(revision.approved_at).toLocaleString("no-NO", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {revision.approved_by && (
                            <> {t("av")} {revision.approved_by.displayTitle}</>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Revision selector and actions */}
                  <div className="flex items-end justify-between">
                    <div className="flex-1 max-w-xs">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t("Velg revisjon")}
                      </label>
                      <select
                        value={revision?.id || ""}
                        onChange={(e) => {
                          const selectedId = parseInt(e.target.value);
                          if (selectedId) {
                            loadRevision(selectedId);
                          }
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      >
                        {allRevisions.length === 0 ? (
                          <option value="">{t("Ingen revisjoner")}</option>
                        ) : (
                          allRevisions.map((rev) => (
                            <option key={rev.id} value={rev.id}>
                              {t("Revisjon")} {rev.revision_number}
                              {rev.is_approved ? ` (${t("Godkjent")})` : ""}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <RoleOnly role="quality" action="approve">
                        {!revision.is_approved && (
                          <button
                            type="button"
                            onClick={handleApproveRevision}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            {t("Godkjenn revisjon")}
                          </button>
                        )}
                      </RoleOnly>
                      <RoleOnly role="quality" action="update">
                        {!isNew && revision.is_approved && (
                          <button
                            type="button"
                            onClick={handleCreateRevision}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                          >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            {t("Ny revisjon")}
                          </button>
                        )}
                      </RoleOnly>
                    </div>
                  </div>

                  {isReadOnly && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm text-yellow-800">
                        {t("Denne revisjonen er godkjent og kan ikke redigeres. Opprett en ny revisjon for å gjøre endringer.")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Canvas */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">{t("Organisasjonskart")}</h3>
                  <div className="flex space-x-2">
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={handleAddNode}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        {t("Legg til modul")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                      disabled={isReadOnly}
                    >
                      {t("Lagre")}
                    </button>
                  </div>
                </div>

                <div
                  ref={canvasRef}
                  className="border-2 border-gray-300 rounded-lg relative bg-gray-50"
                  style={{ width: "100%", height: "800px", overflow: "auto" }}
                  onClick={(e) => {
                    // Don't close context menu if clicking on it
                    if (!e.target.closest('.context-menu-container')) {
                      setSelectedNode(null);
                      setSelectedLink(null);
                      setContextMenu(null);
                      setLinkContextMenu(null);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu(null);
                    setLinkContextMenu(null);
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    style={{ position: "absolute", top: 0, left: 0 }}
                  >
                    <defs>
                      <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="10"
                        refX="9"
                        refY="3"
                        orient="auto"
                      >
                        <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
                      </marker>
                    </defs>
                    {links.map((link) => renderLink(link))}
                    {/* Temporary link line while dragging */}
                    {isLinking && linkFromNode && (() => {
                      const fromNode = getNodeById(linkFromNode);
                      if (!fromNode) return null;
                      const fromPos = getConnectionPointPosition(fromNode, linkFromSide);
                      
                      // Calculate orthogonal path for temporary link
                      const tempToPos = { x: linkMousePos.x, y: linkMousePos.y };
                      const tempPathData = calculateOrthogonalPath(fromPos, tempToPos, linkFromSide, 'top');
                      
                      return (
                        <path
                          d={tempPathData}
                          stroke="#3B82F6"
                          strokeWidth={2}
                          strokeDasharray="5,5"
                          opacity={0.7}
                          fill="none"
                        />
                      );
                    })()}
                  </svg>

                  {nodes.map((node) => (
                    <div
                      key={node.id}
                      style={{
                        position: "absolute",
                        left: node.x_position,
                        top: node.y_position,
                        width: node.width,
                        height: node.height,
                        backgroundColor: node.color,
                        border:
                          selectedNode?.id === node.id
                            ? "3px solid #3B82F6"
                            : "2px solid #1E40AF",
                        borderRadius: "8px",
                        padding: "8px",
                        cursor: isReadOnly ? "default" : "move",
                        zIndex: selectedNode?.id === node.id ? 1000 : node.z_index,
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 2) {
                          // Right click - handled in handleNodeMouseDown
                          handleNodeMouseDown(e, node);
                        } else {
                          // Left click
                          handleNodeMouseDown(e, node);
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNodeMouseDown(e, node);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Don't handle node click when linking - let connection points handle it
                        if (!isLinking) {
                          setSelectedNode(node);
                        }
                      }}
                    >
                      {/* Connection Points */}
                      {!isReadOnly && (
                        <>
                          {/* Top */}
                          <div
                            className="connection-point absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-600 hover:scale-125 transition-all z-50"
                            style={{
                              boxShadow: hoveredConnectionPoint?.nodeId === node.id && hoveredConnectionPoint?.side === 'top' ? '0 0 10px #3B82F6' : 'none',
                            }}
                            onClick={(e) => {
                              if (isLinking && linkFromNode) {
                                handleCompleteLink(node.id, 'top', e);
                              } else {
                                handleStartLink(node.id, 'top', e);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint({ nodeId: node.id, side: 'top' });
                              }
                            }}
                            onMouseLeave={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint(null);
                              }
                            }}
                          />
                          {/* Right */}
                          <div
                            className="connection-point absolute -right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-600 hover:scale-125 transition-all z-50"
                            style={{
                              boxShadow: hoveredConnectionPoint?.nodeId === node.id && hoveredConnectionPoint?.side === 'right' ? '0 0 10px #3B82F6' : 'none',
                            }}
                            onClick={(e) => {
                              if (isLinking && linkFromNode) {
                                handleCompleteLink(node.id, 'right', e);
                              } else {
                                handleStartLink(node.id, 'right', e);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint({ nodeId: node.id, side: 'right' });
                              }
                            }}
                            onMouseLeave={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint(null);
                              }
                            }}
                          />
                          {/* Bottom */}
                          <div
                            className="connection-point absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-600 hover:scale-125 transition-all z-50"
                            style={{
                              boxShadow: hoveredConnectionPoint?.nodeId === node.id && hoveredConnectionPoint?.side === 'bottom' ? '0 0 10px #3B82F6' : 'none',
                            }}
                            onClick={(e) => {
                              if (isLinking && linkFromNode) {
                                handleCompleteLink(node.id, 'bottom', e);
                              } else {
                                handleStartLink(node.id, 'bottom', e);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint({ nodeId: node.id, side: 'bottom' });
                              }
                            }}
                            onMouseLeave={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint(null);
                              }
                            }}
                          />
                          {/* Left */}
                          <div
                            className="connection-point absolute -left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white cursor-crosshair hover:bg-blue-600 hover:scale-125 transition-all z-50"
                            style={{
                              boxShadow: hoveredConnectionPoint?.nodeId === node.id && hoveredConnectionPoint?.side === 'left' ? '0 0 10px #3B82F6' : 'none',
                            }}
                            onClick={(e) => {
                              if (isLinking && linkFromNode) {
                                handleCompleteLink(node.id, 'left', e);
                              } else {
                                handleStartLink(node.id, 'left', e);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint({ nodeId: node.id, side: 'left' });
                              }
                            }}
                            onMouseLeave={() => {
                              if (isLinking) {
                                setHoveredConnectionPoint(null);
                              }
                            }}
                          />
                        </>
                      )}
                      <div className="text-white font-medium text-sm mb-1">
                        {node.title}
                      </div>
                      {node.description && (
                        <div className="text-white text-xs opacity-90">
                          {node.description}
                        </div>
                      )}
                      {node.users && node.users.length > 0 && (
                        <div className="text-white text-xs mt-1 opacity-75">
                          {node.users.length === 1 
                            ? node.users[0].displayTitle
                            : `${node.users.length} ${t("ansatte")}`
                          }
                        </div>
                      )}

                      {/* Resize handles */}
                      {selectedNode?.id === node.id && !isReadOnly && (
                        <>
                          <div
                            style={{
                              position: "absolute",
                              right: -5,
                              top: -5,
                              width: 10,
                              height: 10,
                              backgroundColor: "#3B82F6",
                              border: "2px solid white",
                              borderRadius: "50%",
                              cursor: "nwse-resize",
                            }}
                            onMouseDown={(e) => handleResizeStart(e, "se")}
                          />
                          <div
                            style={{
                              position: "absolute",
                              right: -5,
                              bottom: -5,
                              width: 10,
                              height: 10,
                              backgroundColor: "#3B82F6",
                              border: "2px solid white",
                              borderRadius: "50%",
                              cursor: "nesw-resize",
                            }}
                            onMouseDown={(e) => handleResizeStart(e, "ne")}
                          />
                          <div
                            style={{
                              position: "absolute",
                              left: -5,
                              top: -5,
                              width: 10,
                              height: 10,
                              backgroundColor: "#3B82F6",
                              border: "2px solid white",
                              borderRadius: "50%",
                              cursor: "nesw-resize",
                            }}
                            onMouseDown={(e) => handleResizeStart(e, "nw")}
                          />
                          <div
                            style={{
                              position: "absolute",
                              left: -5,
                              bottom: -5,
                              width: 10,
                              height: 10,
                              backgroundColor: "#3B82F6",
                              border: "2px solid white",
                              borderRadius: "50%",
                              cursor: "nwse-resize",
                            }}
                            onMouseDown={(e) => handleResizeStart(e, "sw")}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Context Menu for Nodes */}
                {contextMenu && !isReadOnly && (
                  <div
                    className="context-menu-container fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1 min-w-[200px]"
                    style={{
                      left: contextMenu.x,
                      top: contextMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditNode(contextMenu.node);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      {t("Rediger")}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNode(contextMenu.node.id);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {t("Slett")}
                    </button>
                  </div>
                )}

                {/* Context Menu for Links */}
                {linkContextMenu && !isReadOnly && (
                  <div
                    className="context-menu-container fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1 min-w-[200px]"
                    style={{
                      left: linkContextMenu.x,
                      top: linkContextMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-200">
                      {t("Dobbelklikk på linje for å legge til punkt")}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLink(linkContextMenu.link.id);
                        setLinkContextMenu(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {t("Slett linje")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </DashboardHeader>

          {/* Edit Node Dialog */}
          <PMDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setEditingNode(null);
            }}
            title={t("Rediger modul")}
            size="md"
          >
            {editingNode && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Tittel")}
                  </label>
                  <input
                    type="text"
                    value={editingNode.title}
                    onChange={(e) =>
                      setEditingNode({ ...editingNode, title: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Beskrivelse")}
                  </label>
                  <textarea
                    value={editingNode.description || ""}
                    onChange={(e) =>
                      setEditingNode({
                        ...editingNode,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Farge")}
                  </label>
                  <input
                    type="color"
                    value={editingNode.color}
                    onChange={(e) =>
                      setEditingNode({ ...editingNode, color: e.target.value })
                    }
                    className="w-full h-10 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Ansatte")}
                  </label>
                  <PMCustomSelector
                    name="user_ids"
                    label={t("Velg ansatte")}
                    title={t("Velg ansatte")}
                    description={t("Velg en eller flere ansatte for denne modulen")}
                    multiple={true}
                    field="displayTitle"
                    field2="email"
                    field3="mobile"
                    fetchFunction={async () => {
                      try {
                        const response = await fetchUsers({
                          filters: [{ name: "EMPLOYEE", value: true }],
                          per_page: 100,
                        });
                        console.log("Fetched users response:", response);
                        // fetchUsers returns response.data, which might be { data: [...] } or just [...]
                        const users = response?.data?.data || response?.data || response || [];
                        console.log("Users array:", users);
                        return users;
                      } catch (error) {
                        console.error("Error fetching users:", error);
                        return [];
                      }
                    }}
                    selectedItems={editingNode.user_ids || []}
                    onChange={(e) => {
                      console.log("PMCustomSelector onChange:", e);
                      const selectedIds = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                      const selectedUsers = e.target.objects || e.target.data || [];
                      setEditingNode({
                        ...editingNode,
                        user_ids: selectedIds.filter(id => id),
                        users: selectedUsers,
                      });
                    }}
                    values={{ user_ids: editingNode.user_ids || [] }}
                    setFieldValue={(name, value) => {
                      console.log("PMCustomSelector setFieldValue:", name, value);
                      const selectedIds = Array.isArray(value) ? value : [value];
                      setEditingNode({
                        ...editingNode,
                        user_ids: selectedIds.filter(id => id),
                      });
                    }}
                  />
                  {editingNode.users && editingNode.users.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {editingNode.users.map((user) => (
                        <div
                          key={user.id}
                          className="inline-flex items-center px-2 py-1 mr-2 mb-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                        >
                          {user.displayTitle}
                          <button
                            type="button"
                            onClick={() => {
                              const updatedUsers = editingNode.users.filter(u => u.id !== user.id);
                              const updatedIds = editingNode.user_ids.filter(id => id !== user.id);
                              setEditingNode({
                                ...editingNode,
                                users: updatedUsers,
                                user_ids: updatedIds,
                              });
                            }}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setEditDialogOpen(false);
                      setEditingNode(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t("Avbryt")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveNodeEdit}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                  >
                    {t("Lagre")}
                  </button>
                </div>
              </div>
            )}
          </PMDialog>
        </Form>
      )}
    </Formik>
  );
};
